/**
 * One timestamp domain for snapshot-testing sort and display (QA-10 F60).
 *
 * The GraphQL date wire path can deliver `updatedAt` as an epoch-millisecond string ("1767225600000"),
 * an ISO string, a number or a Date. `new Date("1767225600000")` is Invalid Date, and short numeric
 * strings such as "0" are parsed by some engines as a calendar year, so numeric text is always read
 * as epoch milliseconds before any string parsing.
 *
 * - Date            -> its time value
 * - finite number   -> epoch milliseconds (0 is the epoch, not "missing")
 * - integer string  -> epoch milliseconds (optional leading minus, surrounding whitespace ignored)
 * - other string    -> Date.parse (ISO-8601 and RFC 2822 forms)
 * - null / undefined / empty string / unparseable -> null (missing or invalid)
 */
export type SnapshotTimestampInput = string | number | Date | null | undefined;

const EPOCH_MS_TEXT = /^-?\d+$/;

export function toSnapshotTimestampMs(value: SnapshotTimestampInput): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  let time: number;
  if (value instanceof Date) {
    time = value.getTime();
  } else if (typeof value === 'number') {
    time = value;
  } else if (typeof value === 'string') {
    const text = value.trim();
    if (!text) {
      return null;
    }
    time = EPOCH_MS_TEXT.test(text) ? Number(text) : Date.parse(text);
  } else {
    return null;
  }
  // Outside the ECMAScript Date range (+-8.64e15 ms) the value cannot be displayed either.
  return Number.isFinite(time) && Math.abs(time) <= 8.64e15 ? time : null;
}

export function toSnapshotDate(value: SnapshotTimestampInput): Date | undefined {
  const time = toSnapshotTimestampMs(value);
  return time === null ? undefined : new Date(time);
}

/** Newest first; missing or invalid timestamps sort after every valid one and keep their relative order. */
export function compareTimestampsNewestFirst(a: SnapshotTimestampInput, b: SnapshotTimestampInput): number {
  const timeA = toSnapshotTimestampMs(a);
  const timeB = toSnapshotTimestampMs(b);
  if (timeA === null || timeB === null) {
    return timeA === null ? (timeB === null ? 0 : 1) : -1;
  }
  return timeB - timeA;
}
